import {
  CallState,
  type CallStateLeavingSubState,
  type CallStatePhoneCallLeavingSubState,
  CallTransition,
  type CallStateLeavingAfterErrorSubState,
  type CallEvents,
} from '../../features/call/call.types';
import {
  useCallStateInitializing,
  useCallStateWaitingParticipants,
  useCallStateInProgress,
  useCallStateRestarting,
  useCallStatePhoneCallParticipants,
} from './states';
import { singletonHook } from 'react-singleton-hook';
import type { CallEventData, TransitionFunc } from './useCall.types';
import { CallEndReason, useTrackCallEndEvent } from '../useTracking';
import { useActor } from '@xstate/react';
import { callActor } from '../../features/call';
import { useStateChangeEffect } from '../useStateChangeEffect';
import { useMemo, useRef, useState } from 'react';
import {
  useRedirectAfterCallSuccess,
  useEndCallNotification,
  useRedirectAfterCallError,
  useNotificationPatientLeft,
  useWaitForParticipantVideo,
  useUpdateRealtimeStateAfterHangUp,
  useNotificationPatientJoined,
  useEndSession,
} from './actions';
import {
  useCallParticipants,
  type CallParticipant,
} from '../useCallParticipants';
import { dismissNotification } from '../../features/notifications/notifications.slice';
import { useAppDispatch } from '../useReduxToolkit';
import { usePictureInPicture } from '../usePictureInPicture';
import { callInitTimer, CallInitStep } from './states/callInitTimer';
import {
  rebootIntercom,
  shutdownIntercom,
} from '../../features/intercom/intercom.utils';
import { useAppSelector } from '../useReduxToolkit';
import { getRole } from '../../features/realtime/realtime.selectors';
import { RealtimeRole } from '@doxyme/realtime';
import { useTestPatient } from '../useTestPatient';
import { useHotpot } from '@doxyme/hotpot/react';
import { getApolloSessionDetails } from '../../features/apolloSession/apolloSession.selectors';
import { useRedirect } from '../useRedirect';

type UseCallStateReturnValue = {
  transition: TransitionFunc;
  callState: CallState;
};

const hookInitValues = {
  transition: async (): Promise<void> => undefined,
  callState: CallState.Unmounted,
};

const LEAVING_AFTER_ERROR_TIMEOUT = 3000;

export const useCallStateImpl = (): UseCallStateReturnValue => {
  const [callState, send] = useActor(callActor);

  const role = useAppSelector(getRole);
  const {
    endSession,
    endSessionBecauseProviderLeft,
    endPatientSessionForTransfer,
  } = useEndSession();
  const { redirectAfterProviderLeft } = useRedirectAfterCallSuccess();
  const redirectAfterCallError = useRedirectAfterCallError();
  const showEndCallNotification = useEndCallNotification();
  const callStateInitializing = useCallStateInitializing(onTransition);
  const showPatientLeftNotification = useNotificationPatientLeft();
  const showPatientJoinedNotification = useNotificationPatientJoined();
  const callStateWaitingParticipants =
    useCallStateWaitingParticipants(onTransition);
  const callStateInProgress = useCallStateInProgress();
  const waitForParticipantVideo = useWaitForParticipantVideo();
  const callStateRestarting = useCallStateRestarting(onTransition);
  const { redirectToDashboardPage } = useRedirect();

  const trackCallEndEvent = useTrackCallEndEvent();
  const updateRealtimeStateAfterHangUp = useUpdateRealtimeStateAfterHangUp();
  const dispatch = useAppDispatch();
  const { destroyPictureInPicture } = usePictureInPicture();
  const { callParticipants } = useCallParticipants();

  const [waitForVideoPromise, setWaitForVideoPromise] =
    useState<Promise<void>>();

  const { stopTestCall } = useTestPatient();

  const callStateName = (
    typeof callState.value === 'string'
      ? callState.value
      : Object.keys(callState.value)[0]
  ) as CallState;

  useCallStatePhoneCallParticipants(onTransition, callStateName);

  const patientLeftNotificationIds = useRef<string[]>([]);

  // for hotpot
  const { dbSessionId } = useAppSelector(getApolloSessionDetails);
  const hotpot = useHotpot();

  /**
   * This block handles the state changes, each state has two phases:
   * 1. The business logic is executed from an individual hook, contained in the states folder
   * 2. After waiting for the logic to finish, we decide what transition to set on the state
   *
   * NOTE: To transition to the next state you need to call the onTransition function
   *
   * @param state the current CallState from selector
   */
  const onStateChange = async (): Promise<void> => {
    switch (callStateName) {
      case CallState.Initializing:
      case CallState.Resuming:
      case CallState.PhoneCallInitializing: {
        await callStateInitializing();
        break;
      }
      case CallState.PhoneCallRinging: {
        break;
      }
      case CallState.TestCallInitializing: {
        /**
         * Left empty explicitly to highlight that we have those states but do not need to handle as
         * they are for the mock call experience.
         */
        break;
      }

      case CallState.Restarting: {
        void callStateRestarting();
        void destroyPictureInPicture();
        break;
      }

      case CallState.WaitingForParticipantsAfterRestart:
      case CallState.WaitingForParticipantsAfterResume:
      case CallState.WaitingForParticipants: {
        const hasAlreadyParticipants = await callStateWaitingParticipants();
        if (hasAlreadyParticipants) {
          await waitForVideoPromise;

          void onTransition(CallTransition.PARTICIPANTS_ALREADY_IN);
        }
        break;
      }
      case CallState.Leaving: {
        void destroyPictureInPicture();

        const callStateLeavingValue =
          callState.value as CallStateLeavingSubState;

        /*
         There's a chance that patients start leaving a call before the provider hangs up.
         When that happens - we will show patient left notifications for each patient that left before the provider ended the call.
         We want to remove any patient left notifications we may have queued up to allow us to immediately show the end call notification, if applicable.
         */
        for (const notificationId of patientLeftNotificationIds.current) {
          void dispatch(dismissNotification({ id: notificationId }));
        }

        if (role === RealtimeRole.Provider) {
          rebootIntercom();
        }

        patientLeftNotificationIds.current = [];
        switch (callStateLeavingValue[CallState.Leaving]) {
          case 'hangUp': {
            // for hotpot - end call for all participants when provider hangs up
            if (role === RealtimeRole.Provider && dbSessionId) {
              void Promise.all(
                callParticipants.map(async (participant) => {
                  try {
                    await hotpot?.visits.call.leaveByRealtimeId({
                      realtimeId: participant.id,
                      sessionId: dbSessionId,
                    });
                    void hotpot?.visits.endCallByRealtimeId({
                      realtimeId: participant.id,
                      sessionId: dbSessionId,
                    });
                  } catch (_error) {
                    // error will be logged by hotpot
                  }
                }),
              );
            }

            trackCallEndEvent(CallEndReason.Cancel);
            await updateRealtimeStateAfterHangUp();
            await endSession();
            break;
          }
          // Applies to all patients. The provider would have left unexpectedly such as window refresh.
          case 'providerLeft': {
            trackCallEndEvent(CallEndReason.ProviderLeft);
            await endSessionBecauseProviderLeft();
            showEndCallNotification();
            redirectAfterProviderLeft();
            break;
          }
          // Applies to the patient who was removed. The provider and remaining patients on a call follow the PATIENT_LEFT flow.
          case 'groupCallParticipantRemoved': {
            trackCallEndEvent(CallEndReason.RemovedFromRoom);
            await endSession();
            break;
          }
          // Applies only to the provider given there was only one patient remaining, who has now left. The patient who left will follow the hangUp flow.
          case 'lastPatientLeft': {
            // for hotpot
            if (
              role === RealtimeRole.Provider &&
              callState.event.type === CallTransition.LAST_PATIENT_LEFT &&
              dbSessionId
            ) {
              const participantId = callState.event.participant.id;
              try {
                await hotpot?.visits.call.leaveByRealtimeId({
                  realtimeId: participantId,
                  sessionId: dbSessionId,
                });
                void hotpot?.visits.endCallByRealtimeId({
                  realtimeId: participantId,
                  sessionId: dbSessionId,
                });
              } catch (_error) {
                // error will be logged by hotpot
              }
            }

            trackCallEndEvent(CallEndReason.PatientLeft);
            /**
             * Maybe in the future we would use context but for now we can rely on the even.
             */
            await endSession();
            if (callState.event.type === CallTransition.LAST_PATIENT_LEFT) {
              // logic for participant left notification removed here
            }
            break;
          }

          // Applies to patient who have had the call ended by the provider while on hold
          case 'hangUpOnHold': {
            // for hotpot
            if (role === RealtimeRole.Provider && dbSessionId) {
              void Promise.all(
                callParticipants.map(async (participant) => {
                  try {
                    await hotpot?.visits.call.leaveByRealtimeId({
                      realtimeId: participant.id,
                      sessionId: dbSessionId,
                    });
                    void hotpot?.visits.endCallByRealtimeId({
                      realtimeId: participant.id,
                      sessionId: dbSessionId,
                    });
                  } catch (_error) {
                    // error will be logged by hotpot
                  }
                }),
              );
            }

            trackCallEndEvent(CallEndReason.HangUpOnHold);

            await endSession();
            break;
          }
          default:
        }
        break;
      }
      /**
       * ONLY FOR PROVIDER
       */
      case CallState.PhoneCallLeaving: {
        void destroyPictureInPicture();

        rebootIntercom();

        const phoneCallLeavingValue =
          callState.value as CallStatePhoneCallLeavingSubState;

        switch (phoneCallLeavingValue[CallState.PhoneCallLeaving]) {
          case 'providerCanceled':
          case 'providerCanceledRinging': {
            trackCallEndEvent(CallEndReason.Cancel);
            break;
          }
          case 'patientBusy': {
            trackCallEndEvent(CallEndReason.PhonePatientBusy);
            break;
          }
          case 'patientUnanswered': {
            trackCallEndEvent(CallEndReason.PhonePatientUnanswered);
            break;
          }
          case 'patientCanceled': {
            trackCallEndEvent(CallEndReason.PatientLeft);
            break;
          }
          default:
        }

        await updateRealtimeStateAfterHangUp();
        await endSession();
        if (
          phoneCallLeavingValue[CallState.PhoneCallLeaving] ===
          'providerCanceledRinging'
        ) {
          redirectToDashboardPage(false);
        }
        break;
      }
      case CallState.LeavingAfterTransferToNewRoom: {
        void destroyPictureInPicture();
        trackCallEndEvent(CallEndReason.TransferToNewRoom);
        await endPatientSessionForTransfer();
        break;
      }

      case CallState.InProgress:
      case CallState.PhoneCallInProgress: {
        if (role === RealtimeRole.Provider) {
          shutdownIntercom();
        }
        void callStateInProgress();
        break;
      }

      case CallState.TestCallInProgress: {
        /**
         * Left empty explicitly to highlight that we have those states but do not need to handle as
         * they are for the mock call experience.
         */
        break;
      }

      case CallState.TestCallLeaving: {
        stopTestCall(CallState.TestCallLeaving);
        break;
      }

      case CallState.TestCallLeavingAfterError: {
        stopTestCall(CallState.TestCallLeavingAfterError);
        break;
      }

      case CallState.PhoneCallLeavingAfterError: {
        void destroyPictureInPicture();
        callInitTimer.finish(CallInitStep.CallFailed);
        trackCallEndEvent(CallEndReason.Default);
        await new Promise((resolve) =>
          setTimeout(resolve, LEAVING_AFTER_ERROR_TIMEOUT),
        );
        await endSession();
        await redirectAfterCallError();
        rebootIntercom();
        break;
      }

      case CallState.LeavingAfterError: {
        void destroyPictureInPicture();

        const callStateLeavingAfterErrorValue =
          callState.value as CallStateLeavingAfterErrorSubState;

        switch (callStateLeavingAfterErrorValue[CallState.LeavingAfterError]) {
          case 'timeout': {
            callInitTimer.finish(CallInitStep.CallFailed);
            trackCallEndEvent(CallEndReason.Timeout);
            break;
          }

          case 'error': {
            trackCallEndEvent(CallEndReason.Default);
            break;
          }

          default:
        }
        await new Promise((resolve) =>
          setTimeout(resolve, LEAVING_AFTER_ERROR_TIMEOUT),
        );
        await endSession();
        await redirectAfterCallError();
        if (role === RealtimeRole.Provider) {
          rebootIntercom();
        }
        break;
      }

      default: {
        break;
      }
    }
  };

  useStateChangeEffect(onStateChange, callStateName);

  /**
   * Very similar to onStateChange, but this one handles the transitions, should otherwise
   * keep the same structure as onStateChange. If a transition doesn't need any logic to be executed
   * do not add it to the switch statement, it will be handled by the state machine.
   *
   * NOTE: In the switch statement to transition to the next state use the onTransition function,
   * be ware of possible loops with the recursive call.
   *
   * @param type the transition type
   * @param eventData the event data, if any
   */
  async function onTransition(
    type: CallTransition,
    eventData?: CallEventData,
  ): Promise<void> {
    switch (type) {
      case CallTransition.PATIENT_LEFT: {
        if (
          eventData?.participant &&
          !patientLeftNotificationIds.current.includes(eventData.participant.id)
        ) {
          patientLeftNotificationIds.current.push(eventData.participant.id);

          if (callStateName === CallState.InProgress) {
            // for hotpot
            if (role === RealtimeRole.Provider && dbSessionId) {
              try {
                await hotpot?.visits.call.leaveByRealtimeId({
                  realtimeId: eventData.participant.id,
                  sessionId: dbSessionId,
                });
                void hotpot?.visits.endCallByRealtimeId({
                  realtimeId: eventData.participant.id,
                  sessionId: dbSessionId,
                });
              } catch (_error) {
                // error will be logged by hotpot
              }
            }

            showPatientLeftNotification(eventData.participant);
          }
        }
        break;
      }

      case CallTransition.INIT_FAILED: {
        callInitTimer.finish(CallInitStep.CallFailed);
        break;
      }
      case CallTransition.PARTICIPANT_JOINED: {
        // Call hotpotVisitStartCall for each patient who joins (provider-side only)
        if (
          role === RealtimeRole.Provider &&
          eventData?.participant &&
          dbSessionId
        ) {
          void hotpot?.visits.call.joinByRealtimeId({
            sessionId: dbSessionId,
            realtimeId: eventData?.participant.id,
          });
        }

        if (callParticipants.length === 1) {
          /**
           * @TODO consider redo it as it should not delay call start when someone already joined
           * because the person could already see you but you still not able to see the person.
           * All video fixes should be happening during the call.
           */
          const waitForVideo = waitForParticipantVideo(eventData?.participant);
          setWaitForVideoPromise(waitForVideo);

          await waitForVideo;
        }

        break;
      }

      case CallTransition.PARTICIPANT_USERNAME_TRANSFERRED: {
        void showPatientJoinedNotification(
          eventData?.participant as CallParticipant,
        );
        break;
      }

      default:
    }

    send({ type, ...eventData } as CallEvents);
  }

  return useMemo(
    () => ({
      transition: onTransition,
      callState: callStateName,
    }),
    [callStateName],
  );
};

/**
 * useCallState - This hook allows observing changes in the
 * state of an ongoing call and transitioning to next states
 *
 * @returns an object with a transition method and the call state.
 *
 */
export const useCallState = singletonHook<UseCallStateReturnValue>(
  hookInitValues,
  useCallStateImpl,
);
